'use client';

import type { FunctionGraphBlock } from '@jeraisonne/ai/visual-blocks';
import { evalExpr } from './constrained-geometry/expr';
import { ImageVisualBlockRenderer } from './image-visual-block';
import { makePlotView } from './plot/projection';
import { sampleFunctionPath } from './plot/sample-function';
import { niceStep, ticks, frenchTick } from './plot/grid';

const VB_W = 360;
const VB_H = 300;
const PAD = 6;
const DEFAULT_COLORS = ['#3b82f6', '#ef4444', '#22c55e', '#f59e0b', '#8b5cf6', '#ec4899'];

/** Évaluateur sûr d'une expression en `x` : NaN si invalide (→ rupture gérée). */
function safeFn(expression: string): (x: number) => number {
  return (x: number) => {
    try {
      const v = evalExpr(expression, { x });
      return Number.isFinite(v) ? v : NaN;
    } catch {
      return NaN;
    }
  };
}

/**
 * Rendu SVG pur d'un graphe de fonction (remplace JSXGraph). Échantillonnage
 * adaptatif des fonctions analytiques, polylignes pour les courbes échantillonnées,
 * grille + axes gradués. Déterministe (SSR/capture). Projection non isotrope
 * (remplit la boîte, comme l'ancienne boundingbox JSXGraph).
 */
/** Largeur approchée d'une graduation (police 9) en unités du viewBox. */
const tickWidth = (label: string) => label.length * 5.2;

export function FunctionGraphBlockRenderer({ block }: { block: FunctionGraphBlock }) {
  const vp = block.viewport ?? { xMin: -5, xMax: 5, yMin: -5, yMax: 5 };
  const showAxis0 = block.axis ?? true;
  // Axes au bord de la fenêtre (x ≥ 0, y ≥ 0 : distances, masses…) : on réserve la place des graduations,
  // sinon celles de l'axe vertical sortent à gauche et la dernière de l'axe horizontal à droite.
  const yTickLabels = ticks(vp.yMin, vp.yMax, niceStep(vp.yMax - vp.yMin))
    .filter((y) => y !== 0)
    .map(frenchTick);
  const xTickLabels = ticks(vp.xMin, vp.xMax, niceStep(vp.xMax - vp.xMin))
    .filter((x) => x !== 0)
    .map(frenchTick);
  const nearLeft = vp.xMin <= 0 && vp.xMax >= 0 && -vp.xMin < (vp.xMax - vp.xMin) * 0.12;
  const nearBottom = vp.yMin <= 0 && vp.yMax >= 0 && -vp.yMin < (vp.yMax - vp.yMin) * 0.12;
  const pad = {
    left: showAxis0 && nearLeft ? PAD + Math.max(0, ...yTickLabels.map(tickWidth)) + 4 : PAD,
    right: showAxis0 ? PAD + tickWidth(xTickLabels.at(-1) ?? '') / 2 : PAD,
    top: PAD,
    bottom: showAxis0 && nearBottom ? PAD + 10 : PAD,
  };
  const view = makePlotView(vp, { w: VB_W, h: VB_H }, { pad });
  // place d’une graduation horizontale : sa largeur + 10 (lisible à 390 px) ; sinon une sur deux
  const xGap = view.sx(niceStep(vp.xMax - vp.xMin));
  const xLabelEvery = Math.max(0, ...xTickLabels.map(tickWidth)) + 10 > xGap ? 2 : 1;

  const functions = (block.functions ?? []).filter((f) => f.expression);
  const curves = (block.curves ?? []).filter((c) => c.sampledPoints && c.sampledPoints.length > 0);
  const points = block.points ?? [];

  if (
    functions.length === 0 &&
    curves.length === 0 &&
    points.length === 0 &&
    block.source?.imageSrc
  ) {
    return (
      <ImageVisualBlockRenderer
        block={{ ...block, type: 'image_visual', src: block.source.imageSrc }}
      />
    );
  }

  const showGrid = block.grid ?? true;
  const showAxis = block.axis ?? true;
  const stepX = niceStep(vp.xMax - vp.xMin);
  const stepY = niceStep(vp.yMax - vp.yMin);
  const xAxisVisible = vp.yMin <= 0 && vp.yMax >= 0;
  const yAxisVisible = vp.xMin <= 0 && vp.xMax >= 0;

  return (
    <div className="w-full overflow-x-auto">
      <svg
        viewBox={`0 0 ${VB_W} ${VB_H}`}
        className="mx-auto w-full max-w-lg"
        style={{ maxHeight: '320px' }}
        role="img"
        aria-label={block.caption ?? block.title ?? 'Graphe de fonction'}
      >
        {/* Grille */}
        {showGrid &&
          ticks(vp.xMin, vp.xMax, stepX).map((x) => (
            <line
              key={`gx-${x}`}
              x1={view.tx(x)}
              y1={view.ty(vp.yMax)}
              x2={view.tx(x)}
              y2={view.ty(vp.yMin)}
              stroke="#eef2f7"
              strokeWidth={1}
            />
          ))}
        {showGrid &&
          ticks(vp.yMin, vp.yMax, stepY).map((y) => (
            <line
              key={`gy-${y}`}
              x1={view.tx(vp.xMin)}
              y1={view.ty(y)}
              x2={view.tx(vp.xMax)}
              y2={view.ty(y)}
              stroke="#eef2f7"
              strokeWidth={1}
            />
          ))}

        {/* Axes (tracés si 0 est dans le viewport) */}
        {showAxis && xAxisVisible && (
          <line
            x1={view.tx(vp.xMin)}
            y1={view.ty(0)}
            x2={view.tx(vp.xMax)}
            y2={view.ty(0)}
            stroke="#94a3b8"
            strokeWidth={1.5}
          />
        )}
        {showAxis && yAxisVisible && (
          <line
            x1={view.tx(0)}
            y1={view.ty(vp.yMin)}
            x2={view.tx(0)}
            y2={view.ty(vp.yMax)}
            stroke="#94a3b8"
            strokeWidth={1.5}
          />
        )}

        {/* Graduations chiffrées */}
        {showAxis &&
          xAxisVisible &&
          ticks(vp.xMin, vp.xMax, stepX)
            .filter((x) => x !== 0)
            // graduations trop serrées (« 35 000 » collé à « 40 000 ») : une sur deux, en gardant la dernière
            .filter((x, k, all) => xLabelEvery === 1 || (all.length - 1 - k) % xLabelEvery === 0)
            .map((x) => (
              <text
                key={`tx-${x}`}
                x={view.tx(x)}
                y={Math.min(view.ty(0) + 11, VB_H - 2)}
                fontSize={9}
                textAnchor="middle"
                fill="#64748b"
              >
                {frenchTick(x)}
              </text>
            ))}
        {showAxis &&
          yAxisVisible &&
          ticks(vp.yMin, vp.yMax, stepY)
            .filter((y) => y !== 0)
            .map((y) => (
              <text
                key={`ty-${y}`}
                x={Math.max(view.tx(0) - 4, 2)}
                y={view.ty(y) + 3}
                fontSize={9}
                textAnchor="end"
                fill="#64748b"
              >
                {frenchTick(y)}
              </text>
            ))}

        {/* Courbes échantillonnées (sampledPoints) */}
        {curves.map((c, i) => {
          const color = c.color ?? DEFAULT_COLORS[i % DEFAULT_COLORS.length]!;
          const d = c
            .sampledPoints!.map(
              (p, j) =>
                `${j === 0 ? 'M' : 'L'} ${view.tx(p[0]).toFixed(2)} ${view.ty(p[1]).toFixed(2)}`,
            )
            .join(' ');
          return <path key={c.id} d={d} fill="none" stroke={color} strokeWidth={2} />;
        })}

        {/* Fonctions analytiques (échantillonnage adaptatif) */}
        {functions.map((f, i) => {
          const color = f.color ?? DEFAULT_COLORS[i % DEFAULT_COLORS.length]!;
          const domain = f.domain ?? [vp.xMin, vp.xMax];
          const d = sampleFunctionPath(safeFn(f.expression!), domain, view);
          if (!d) return null;
          const labelY = f.label ? safeFn(f.expression!)(domain[1]) : NaN;
          const showLabel =
            !!f.label && Number.isFinite(labelY) && labelY >= vp.yMin && labelY <= vp.yMax;
          return (
            <g key={f.id}>
              <path d={d} fill="none" stroke={color} strokeWidth={2} />
              {showLabel && (
                <text
                  x={Math.min(view.tx(domain[1]) - 2, VB_W - 2)}
                  y={view.ty(labelY) - 4}
                  fontSize={11}
                  fontWeight={600}
                  textAnchor="end"
                  fill={color}
                >
                  {f.label}
                </text>
              )}
            </g>
          );
        })}

        {/* Points marqués */}
        {points.map((p) => (
          <g key={p.id}>
            <circle
              cx={view.tx(p.x)}
              cy={view.ty(p.y)}
              r={3.5}
              fill={p.color ?? '#ef4444'}
              stroke="#fff"
              strokeWidth={1}
            />
            {(p.label ?? p.name) && (
              <text
                x={view.tx(p.x) + 6}
                y={view.ty(p.y) - 6}
                fontSize={11}
                fontWeight={600}
                fill={p.color ?? '#ef4444'}
              >
                {p.label ?? p.name}
              </text>
            )}
          </g>
        ))}
      </svg>
    </div>
  );
}
